import React from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { BAND_CONTENT, DIMENSION_LABEL, DIMENSION_ORDER, dimensionLevel } from './scoring';
import { createAuditHref, decodeScoreResult } from './scoreResultUrl';
type ScoreResultsPageProps = {
  search: string;
};
export function ScoreResultsPage({
  search
}: ScoreResultsPageProps) {
  const result = decodeScoreResult(search);
  if (!result) {
    return <EmptyScoreState data-id="element-490" data-mp="3oiembd6fl6eu" />;
  }
  const content = BAND_CONTENT[result.band];
  const auditEmailHref = createAuditHref(result);
  return <main className="min-h-screen w-full bg-paper px-6 py-8 text-text md:px-10 md:py-12" data-id="element-491" data-mp="3sif8jjzrulwg">
      <div className="mx-auto max-w-4xl" data-id="element-492" data-mp="3er0t1n52p2u5">
        <a href="/" className="inline-flex items-center gap-2 rounded-xl px-2 py-2 text-sm font-semibold text-navy transition-colors hover:bg-paper-dark focus:outline-none focus:ring-2 focus:ring-seaglass" data-id="element-493" data-mp="3jqn3xpxfi6zs">
          
          <ArrowLeft className="h-4 w-4" aria-hidden="true" data-id="element-494" data-mp="g21zyfzq4x04" />
          Back to Author Leverage
        </a>

        <header className="border-b border-gray-200 pb-10 pt-10 text-center md:pb-12" data-id="element-495" data-mp="vx7dmg3z3yo4">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-seaglass" data-id="element-496" data-mp="rwx6v18cx487">
            Your Reader-to-Revenue Score
          </p>
          <p className="mt-4 text-sm font-medium text-text-muted" data-id="element-497" data-mp="15pd5lpu86g3b">
            {result.totalScore} of 24 points
          </p>
          <h1 className="mx-auto mt-5 max-w-3xl font-serif text-4xl leading-tight text-navy md:text-6xl" data-id="element-498" data-mp="10v8seoud9bts">
            {result.band}
          </h1>
          <p className="mx-auto mt-7 max-w-2xl text-lg leading-relaxed text-text-muted" data-id="element-499" data-mp="1altr0ca1igcp">
            {content.acknowledgment}
          </p>
          <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-text-muted" data-id="element-500" data-mp="1okjxujmnvue9">
            {content.meaning}
          </p>
        </header>

        <section className="grid gap-5 py-10 text-left md:grid-cols-2 md:py-12" data-id="element-501" data-mp="3qd7vmdgjdpmn">
          <article className="rounded-3xl border border-gray-200 bg-white p-7 md:p-8" data-id="element-502" data-mp="3lf6ehy2o80dt">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-seaglass" data-id="element-503" data-mp="1pbrwasmoz8x1">
              Biggest opportunity
            </p>
            <h2 className="mt-3 font-serif text-3xl text-navy" data-id="element-504" data-mp="1ubnycmbtwq9y">
              {DIMENSION_LABEL[result.biggestOpportunity]}
            </h2>
            <p className="mt-4 leading-relaxed text-text-muted" data-id="element-505" data-mp="1zcbzi0trunhw">
              {content.opportunity}
            </p>
          </article>

          <article className="rounded-3xl border border-gray-200 bg-white p-7 md:p-8" data-id="element-506" data-mp="28b5ph8clkfmh">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-seaglass" data-id="element-507" data-mp="2d9gyxhyzco46">
              Strongest layer
            </p>
            <h2 className="mt-3 font-serif text-3xl text-navy" data-id="element-508" data-mp="2i1s0ep9dchyu">
              {DIMENSION_LABEL[result.strongestLayer]}
            </h2>
            <p className="mt-4 leading-relaxed text-text-muted" data-id="element-509" data-mp="2n1czczojgqbk">
              {content.nextStep}
            </p>
          </article>
        </section>

        <section className="border-t border-gray-200 py-10 text-left md:py-12" aria-labelledby="dimensions-heading" data-id="element-510" data-mp="2w19unk5fonfl">
          
          <p className="mb-4 text-center text-xs font-semibold uppercase tracking-[0.18em] text-seaglass" data-id="element-511" data-mp="ke6dkcsme0pw">
            Your four layers
          </p>
          <h2 id="dimensions-heading" className="sr-only" data-id="element-512" data-mp="bboprtxbh00s">
            Your four score dimensions
          </h2>
          <div className="grid gap-3 sm:grid-cols-2" data-id="element-513" data-mp="6e5coxccutaf">
            {DIMENSION_ORDER.map(dimension => <article key={dimension} className="rounded-2xl border border-gray-200 bg-white px-5 py-4" data-id="element-514" data-mp="133mev2c4px7d">
              
                <div className="flex items-baseline justify-between gap-4" data-id="element-515" data-mp="z32gjvftknjn">
                  <h3 className="font-semibold text-navy" data-id="element-516" data-mp="ua9e5e7tpraq">
                    {DIMENSION_LABEL[dimension]}
                  </h3>
                  <p className="text-sm font-medium text-text-muted" data-id="element-517" data-mp="pbg98a6lr8m6">
                    {result.dimensions[dimension]} / 6
                  </p>
                </div>
                <p className="mt-1 text-sm text-text-muted" data-id="element-518" data-mp="1i0dffexjheyk">
                  {dimensionLevel(result.dimensions[dimension])}
                </p>
              </article>)}
          </div>
        </section>

        <section className="rounded-3xl bg-navy px-7 py-10 text-center text-paper md:px-12" aria-labelledby="audit-heading" data-id="element-519" data-mp="16kh5v4o5zhzc">
          
          <h2 id="audit-heading" className="font-serif text-3xl leading-tight md:text-4xl" data-id="element-520" data-mp="smag6btdcoar">
            
            See what a clearer path could unlock.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-paper/75" data-id="element-521" data-mp="1la6dniqrik8l">
            {content.ctaHelper}
          </p>
          <a href={auditEmailHref} className="mt-7 inline-flex min-h-[56px] items-center justify-center rounded-xl bg-[#d4a574] px-8 py-4 font-bold text-white transition-colors hover:bg-[#c89860] focus:outline-none focus:ring-2 focus:ring-white" data-id="element-522" data-mp="1p9xaa32yzul1">
            
            Explore a Reader Path Audit
            <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" data-id="element-523" data-mp="246oaufodrcc8" />
          </a>
        </section>
      </div>
    </main>;
}
function EmptyScoreState() {
  return <main className="flex min-h-screen w-full items-center justify-center bg-paper px-6 py-12 text-text" data-id="element-524" data-mp="2icpzojb9wnky">
      <section className="w-full max-w-xl rounded-3xl border border-gray-200 bg-white p-8 text-center shadow-sm md:p-12" aria-labelledby="empty-score-heading" data-id="element-525" data-mp="2eauz5h162e88">
        
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-seaglass" data-id="element-526" data-mp="2n4hrex660zco">
          Reader-to-Revenue Score
        </p>
        <h1 id="empty-score-heading" className="mt-4 font-serif text-4xl leading-tight text-navy" data-id="element-527" data-mp="1zfoq0l6rxzao">
          
          This score link is incomplete or invalid.
        </h1>
        <p className="mt-5 leading-relaxed text-text-muted" data-id="element-528" data-mp="29dcn3hruriw0">
          Complete the eight-question check to generate a fresh score page.
        </p>
        <a href="/" className="mt-8 inline-flex min-h-[48px] items-center justify-center rounded-xl bg-[#d4a574] px-6 py-3 font-bold text-white transition-colors hover:bg-[#c89860] focus:outline-none focus:ring-2 focus:ring-seaglass" data-id="element-529" data-mp="24cf6h9mhqwwm">
          
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" data-id="element-530" data-mp="3ntuxn4j24kn6" />
          Return to Author Leverage
        </a>
      </section>
    </main>;
}